import {
  CellContext,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table as TableType,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '../../Checkbox/Checkbox';
import inputStyles from '../../Input/Input.module.css';
import Tooltip from '../../Tooltip/Tooltip';
import sharedTableStyles from '../SharedTableStyles.module.css';
import SortIcon from '../SortIcon';
import styles from './Table.module.css';
import { TableProps } from './Table.types';
import { useTranslation } from 'react-i18next';

const Table = <TData extends object>({
  data,
  columns,
  returnedRecordsText,
  enableFilters = false,
  enableRowCount = false,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  noRowsMessage,
  getTableApi,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  loadingRowCount = 15,
  width,
  height,
  includeMinWidth = true,
  className,
  headersToWrap,
  setRegisterCount,
  setFilteredData,
  meta,
  renderSelectionCell,
  renderRowLeadingCell,
}: TableProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(selectedRows || {});
  const id = useId();

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, unknown>) =>
      column.id === 'id' ? <div /> : <div className={sharedTableStyles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) =>
      renderSelectionCell ? (
        renderSelectionCell({ row: params.row as Row<TData> })
      ) : (
        <ul className={sharedTableStyles.rowCheckbox}>
          <li>
            <Checkbox
              label=""
              aria-label="select row"
              id={`${id}-row-${params.row.id}-checkbox`}
              checked={params.row.getIsSelected()}
              onChange={params.row.getToggleSelectedHandler()}
              disabled={!params.row.getCanSelect()}
            />
          </li>
        </ul>
      ),
    [id, renderSelectionCell],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={sharedTableStyles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${id}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [id],
  );

  const leadingColumn = useMemo(
    () =>
      renderRowLeadingCell
        ? [
            {
              id: 'row-leading',
              header: () => null,
              cell: (params: { row: Row<TData> }) => renderRowLeadingCell({ row: params.row }),
            },
          ]
        : [],
    [renderRowLeadingCell],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      ...leadingColumn,
      {
        id: 'select',
        header: enableMultiRowSelection ? renderHeaderCell : undefined,
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, leadingColumn, renderCheckboxCell, renderHeaderCell],
  );

  const columnsWithLeadingOnly = useMemo(() => [...leadingColumn, ...columns], [columns, leadingColumn]);

  const tableData = useMemo(
    () => (isLoading ? Array(loadingRowCount).fill({}) : data),
    [isLoading, loadingRowCount, data],
  );

  const tableColumns = useMemo(() => {
    if (isLoading) {
      return columns.map((column) => ({
        ...column,
        cell: LoadingSkeleton,
      }));
    }
    if (enableRowSelection) {
      return columnsWithRowSelection;
    }
    if (leadingColumn.length > 0) {
      return columnsWithLeadingOnly;
    }
    return columns;
  }, [
    isLoading,
    enableRowSelection,
    columnsWithRowSelection,
    columnsWithLeadingOnly,
    columns,
    LoadingSkeleton,
    leadingColumn.length,
  ]);

  const table = useReactTable({
    data: tableData || [],
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    meta,
  });

  useEffect(() => {
    if (getTableApi) getTableApi(table);
  }, [getTableApi, table]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRow = table.getFilteredRowModel().rows.map((row) => row.original);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const recordText = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRow.length);
    }
    return t('common.table.row', { count: filteredRow.length });
  }, [returnedRecordsText, filteredRow.length, t]);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  useEffect(() => {
    if (setRegisterCount) {
      setRegisterCount(filteredRow.length);
    }
    setFilteredData?.(filteredRow);
  }, [filteredRow.length, setRegisterCount]);

  return (
    <div className={classNames(styles.tableContainerWrapper, { [className!]: className !== undefined })}>
      <div
        style={{ width, height }}
        ref={tableContainerRef}
        className={classNames(styles.tableContainer, {
          [styles.loading]: isLoading,
          [sharedTableStyles.minWidth]: includeMinWidth,
          [sharedTableStyles.hasScrollbar]: hasScrollbar,
          [sharedTableStyles.tableWithRowLeading]: renderRowLeadingCell !== undefined,
        })}
      >
        <table className={classNames(styles.table, { [styles.withRowSelection]: enableRowSelection })}>
          <thead className={styles.stickyHeader}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    data-testid={`table-header-${header.id}`}
                    key={header.id}
                    className={classNames(sharedTableStyles.columnHeader, {
                      [sharedTableStyles.rowLeadingCell]: header.column.id === 'row-leading',
                    })}
                    aria-sort={getAriaSort(header.column.getIsSorted())}
                  >
                    <div
                      data-testid={`table-sort-button-${header.id}`}
                      className={sharedTableStyles.columnHeaderTextAndIcon}
                      tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                      onClick={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      onTouchStart={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      aria-label={`sort column ${
                        typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                      }`}
                      role="button"
                      onKeyDown={(event: KeyboardEvent) => {
                        switch (event.key) {
                          case ' ':
                          case 'Enter':
                            header.column.toggleSorting();
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <span
                        className={classNames(sharedTableStyles.columnHeaderText, {
                          [sharedTableStyles.wrapHeaderText]: headersToWrap?.includes(`${header.column.columnDef.id}`),
                        })}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.columnDef.enableSorting && <SortIcon />}
                      {header.column.columnDef.meta?.tooltipText !== undefined && (
                        <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                      )}
                    </div>
                    {header.column.getCanFilter() && !header.column.columnDef.meta?.hideFilter ? (
                      <input
                        disabled={header.column.columnDef.meta?.disableFilter}
                        onChange={(event) => header.column.setFilterValue(event.target.value)}
                        value={(header.column.getFilterValue() as string) ?? ''}
                        className={classNames(inputStyles.input, sharedTableStyles.headerFilter)}
                        aria-label={`${header.column.columnDef.header} filter input`}
                        id={`${header.id}filterInput`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className={classNames(sharedTableStyles.row, {
                  [sharedTableStyles.even]: index % 2 === 0,
                  [sharedTableStyles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={classNames(sharedTableStyles.cell, {
                      [sharedTableStyles.rowLeadingCell]: cell.column.id === 'row-leading',
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {enableRowCount && !isLoading && (
          <div className={styles.footer}>
            <h2>{`${filteredRow.length} ${recordText}`}</h2>
          </div>
        )}
      </div>
      {hasScrollbar && <div className={sharedTableStyles.scrollShadow} />}
    </div>
  );
};

export default Table;
